import React, { useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import maplibregl from 'maplibre-gl';
import { toCanvas } from 'html-to-image';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
    aggregateStats,
    elevationProfile,
    mapAreaAspect,
    formatDistance,
    formatElevation,
    fmtDuration,
} from '../../utils/gpx';
import { PALETTE, BORDERS, FONT_FAMILY } from '../../theme';
import { BASEMAPS, TILE_STYLES, rasterStyle, basemapEntry } from '../basemaps';

// Basemaps + tile styles are shared with Road Trips — re-exported here so the
// existing `import TrailMapRender, { TILE_STYLES } from './trail/TrailMapRender'`
// call sites keep working.
export { BASEMAPS, TILE_STYLES };

/**
 * The trail-maps render: the route drawn ON a real basemap, framed by a
 * branded card. Title + stats sit OVER the map (centered title), and the map's
 * fit padding is measured from the actual card heights so the trail is always
 * inset clear of them. The map is NON-interactive (a fixed print preview).
 *
 * BASEMAPS — high-contrast, print-friendly. VECTOR styles (Stadia) render
 * labels client-side with collision avoidance, so place names don't clip at
 * the edges the way baked raster labels do. DEV note: Stadia is keyless from
 * localhost; production needs a free Stadia key (append `?api_key=…` to the
 * style/tiles URLs — one place each). OpenTopoMap is OSM raster.
 *
 * CAPTURE: html-to-image can't read a WebGL canvas, so capture() draws the
 * card chrome (overlay cards + bg, maplibre node filtered out) then composites
 * the map's getCanvas() frame into the map-area rect.
 */
const STAT_DEFS = [
    { key: 'distance', label: 'Distance', get: (s, u) => formatDistance(s.distanceM, u) },
    { key: 'gain', label: 'Elevation gain', get: (s, u) => (s.gainM != null ? `+${formatElevation(s.gainM, u)}` : null) },
    { key: 'loss', label: 'Elevation loss', get: (s, u) => (s.lossM != null ? `-${formatElevation(s.lossM, u)}` : null) },
    { key: 'days', label: 'Days', get: (s) => String(s.days) },
    { key: 'high', label: 'High point', get: (s, u) => (s.eleMax != null ? formatElevation(s.eleMax, u) : null) },
    { key: 'low', label: 'Low point', get: (s, u) => (s.eleMin != null ? formatElevation(s.eleMin, u) : null) },
    { key: 'duration', label: 'Moving time', get: (s) => (s.durationS != null ? fmtDuration(s.durationS) : null) },
];
export const STAT_FIELD_DEFS = STAT_DEFS.map(({ key, label }) => ({ key, label }));

const addRouteLayers = (map, segments, accent, withMarkers) => {
    map.addSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: segments.map((s) => ({ type: 'Feature', geometry: { type: 'LineString', coordinates: s.points.map((p) => [p.lon, p.lat]) } })) } });
    map.addLayer({ id: 'route-casing', type: 'line', source: 'route', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.9 } });
    map.addLayer({ id: 'route', type: 'line', source: 'route', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': accent, 'line-width': 4 } });
    if (withMarkers) {
        const first = segments[0].points[0];
        const lastPts = segments[segments.length - 1].points;
        const last = lastPts[lastPts.length - 1];
        map.addSource('endpoints', { type: 'geojson', data: { type: 'FeatureCollection', features: [
            { type: 'Feature', properties: { kind: 'start' }, geometry: { type: 'Point', coordinates: [first.lon, first.lat] } },
            { type: 'Feature', properties: { kind: 'end' }, geometry: { type: 'Point', coordinates: [last.lon, last.lat] } },
        ] } });
        map.addLayer({ id: 'pt-end', type: 'circle', source: 'endpoints', filter: ['==', 'kind', 'end'], paint: { 'circle-radius': 7, 'circle-color': accent, 'circle-stroke-color': '#fff', 'circle-stroke-width': 3 } });
        map.addLayer({ id: 'pt-start', type: 'circle', source: 'endpoints', filter: ['==', 'kind', 'start'], paint: { 'circle-radius': 7, 'circle-color': '#fff', 'circle-stroke-color': PALETTE.accent, 'circle-stroke-width': 4 } });
    }
};

const TrailMapRender = forwardRef(
    ({ segments, title = 'My Hike', accent = PALETTE.primary, units = 'metric', styleId = 'topographic', show = { title: true, stats: true, elevation: true, markers: true }, statFields = { distance: true, gain: true, days: true, high: true, duration: true }, onReady } , ref) => {
        const cardRef = useRef(null);
        const mapAreaRef = useRef(null);
        const containerRef = useRef(null);
        const titleCardRef = useRef(null);
        const statsCardRef = useRef(null);
        const mapRef = useRef(null);
        const boundsRef = useRef(null);
        const mapSnapshotRef = useRef(null); // fresh 2D snapshot of the map, taken on every 'idle'
        const onReadyRef = useRef(onReady);
        onReadyRef.current = onReady;

        const stats = useMemo(() => aggregateStats(segments), [segments]);
        const prof = useMemo(() => elevationProfile(segments), [segments]);
        const aspect = useMemo(() => mapAreaAspect(segments), [segments]);
        const showElevation = show.elevation && !!prof;
        const entry = basemapEntry(styleId);

        const statItems = STAT_DEFS.filter((d) => statFields[d.key]).map((d) => ({ l: d.label, v: d.get(stats, units) })).filter((d) => d.v != null);

        // Padding measured from the real overlay cards → trail never sits under them.
        const fitToTrack = useCallback(() => {
            const map = mapRef.current;
            if (!map || !boundsRef.current) return;
            const tH = show.title && titleCardRef.current ? titleCardRef.current.offsetHeight : 0;
            const sH = show.stats && statItems.length && statsCardRef.current ? statsCardRef.current.offsetHeight : 0;
            map.fitBounds(boundsRef.current, {
                padding: { top: 18 + (tH ? tH + 28 : 0), bottom: 18 + (sH ? sH + 28 : 0), left: 44, right: 44 },
                maxZoom: 15,
                duration: 0,
            });
        }, [show.title, show.stats, statItems.length]);

        // create / rebuild map (data, basemap, markers)
        useEffect(() => {
            if (!containerRef.current || !segments.length) return undefined;
            const coords = segments.flatMap((s) => s.points.map((p) => [p.lon, p.lat]));
            const lons = coords.map((c) => c[0]);
            const lats = coords.map((c) => c[1]);
            boundsRef.current = [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];

            const map = new maplibregl.Map({
                container: containerRef.current,
                style: entry.kind === 'vector' ? entry.style : rasterStyle(entry),
                bounds: boundsRef.current,
                fitBoundsOptions: { padding: 60, maxZoom: 15 },
                attributionControl: false,
                interactive: false, // fixed print preview — no pan / zoom / scroll
                preserveDrawingBuffer: true,
            });
            mapRef.current = map;
            map.on('load', () => {
                addRouteLayers(map, segments, accent, show.markers);
                fitToTrack();
            });
            // 'idle' = the frame is freshly painted in the preserved buffer — the
            // ONLY reliable moment to read a WebGL canvas. Snapshot it to a 2D
            // canvas now; capture() composites that snapshot later. No redraw, so
            // this can't re-trigger a render (no capture loop).
            map.on('idle', () => {
                try {
                    const cv = map.getCanvas();
                    let snap = mapSnapshotRef.current;
                    if (!snap) { snap = document.createElement('canvas'); mapSnapshotRef.current = snap; }
                    snap.width = cv.width;
                    snap.height = cv.height;
                    snap.getContext('2d').drawImage(cv, 0, 0);
                } catch (e) { /* ignore transient read errors */ }
                if (onReadyRef.current) onReadyRef.current();
            });
            return () => map.remove();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [segments, accent, styleId, show.markers]);

        // refit when card sizes / shape change (no rebuild)
        useEffect(() => {
            const map = mapRef.current;
            if (!map) return;
            if (map.loaded()) fitToTrack();
            else map.once('load', fitToTrack);
        }, [title, units, aspect, fitToTrack]);

        useImperativeHandle(ref, () => ({
            async capture() {
                const map = mapRef.current;
                const cardEl = cardRef.current;
                const mapAreaEl = mapAreaRef.current;
                if (!map || !cardEl || !mapAreaEl) throw new Error('Not ready');
                // Wait until the map has finished rendering (tiles painted into
                // the preserved drawing buffer). Use on/off rather than once() so
                // we don't miss an 'idle' that already fired, with a timeout
                // fallback. Then read the buffer directly — NO requestAnimationFrame
                // (that reads mid-redraw and yields a blank frame).
                // Ensure we have a fresh idle snapshot (the create-effect 'idle'
                // handler takes it). Wait for one if the map is still loading.
                if (!mapSnapshotRef.current || !map.loaded() || !map.areTilesLoaded()) {
                    await new Promise((resolve) => {
                        const onIdle = () => { map.off('idle', onIdle); resolve(); };
                        map.on('idle', onIdle);
                        setTimeout(() => { map.off('idle', onIdle); resolve(); }, 6000);
                    });
                }
                // Composite the snapshot (a clean 2D canvas) — never the live GL
                // canvas, whose buffer may be cleared by the time we read it.
                const mapCanvas = mapSnapshotRef.current || map.getCanvas();
                const mapRect = mapAreaEl.getBoundingClientRect();
                const cardRect = cardEl.getBoundingClientRect();
                const S = mapCanvas.width / mapRect.width;
                // Capture the chrome (title/stats cards, elevation band, border)
                // with a TRANSPARENT card background + the maplibre node removed,
                // so the map area stays clear and the real map frame shows through
                // when composited underneath. (A white card bg here would paint
                // over the map → blank map in the export.)
                const chrome = await toCanvas(cardEl, {
                    pixelRatio: S,
                    skipFonts: true,
                    backgroundColor: undefined,
                    style: { background: 'transparent' },
                    filter: (node) => !(node.classList && node.classList.contains('maplibregl-map')),
                });
                const out = document.createElement('canvas');
                out.width = Math.round(cardRect.width * S);
                out.height = Math.round(cardRect.height * S);
                const ctx = out.getContext('2d');
                ctx.fillStyle = PALETTE.surface;
                ctx.fillRect(0, 0, out.width, out.height);
                ctx.drawImage(mapCanvas, Math.round((mapRect.left - cardRect.left) * S), Math.round((mapRect.top - cardRect.top) * S), Math.round(mapRect.width * S), Math.round(mapRect.height * S));
                ctx.drawImage(chrome, 0, 0, out.width, out.height);
                return await new Promise((r) => out.toBlob(r, 'image/png'));
            },
        }), []);

        const subtitle = `${stats.days > 1 ? `${stats.days}-day traverse` : 'Day hike'} · ${formatDistance(stats.distanceM, units)}${stats.gainM != null ? ` · +${formatElevation(stats.gainM, units)}` : ''}`;
        const overlayCard = { background: 'rgba(255,255,255,0.93)', borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.15)' };

        return (
            <div style={{ width: '100%', maxWidth: 560, margin: '0 auto' }}>
              <div ref={cardRef} style={{ width: '100%', background: PALETTE.surface, borderRadius: 16, overflow: 'hidden', border: BORDERS.faint, fontFamily: FONT_FAMILY }}>
                <div ref={mapAreaRef} style={{ position: 'relative', width: '100%', aspectRatio: `${aspect}` }}>
                    <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

                    {show.title && (
                        <div ref={titleCardRef} style={{ ...overlayCard, position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', maxWidth: '88%', padding: '8px 16px', textAlign: 'center' }}>
                            {title && <div style={{ fontWeight: 800, fontSize: 21, letterSpacing: '-0.01em', color: PALETTE.text, lineHeight: 1.15 }}>{title}</div>}
                            <div style={{ fontWeight: 600, fontSize: 12.5, color: PALETTE.textMuted, marginTop: 2 }}>{subtitle}</div>
                        </div>
                    )}

                    {show.stats && statItems.length > 0 && (
                        <div ref={statsCardRef} style={{ ...overlayCard, position: 'absolute', left: 12, right: 12, bottom: 12, padding: '8px 6px', display: 'flex', justifyContent: 'space-around', gap: 4 }}>
                            {statItems.map((s) => (
                                <div key={s.l} style={{ textAlign: 'center' }}>
                                    <div style={{ fontWeight: 800, fontSize: 17, color: PALETTE.text, whiteSpace: 'nowrap' }}>{s.v}</div>
                                    <div style={{ fontWeight: 700, fontSize: 9, letterSpacing: '0.03em', color: PALETTE.textMuted, marginTop: 2, whiteSpace: 'nowrap' }}>{s.l.toUpperCase()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {showElevation && (
                    <div style={{ padding: '10px 18px 12px', borderTop: `1px solid ${PALETTE.surfaceSubtle}` }}>
                        <ElevationStrip prof={prof} accent={accent} units={units} />
                    </div>
                )}
              </div>
              {/* Credits line — web only, OUTSIDE cardRef so it's not on the printed image. */}
              <div style={{ fontSize: 12, color: PALETTE.textMuted, textAlign: 'center', marginTop: 10, fontFamily: FONT_FAMILY, lineHeight: 1.4 }}>
                Map credits: {entry.attribution || '© OpenStreetMap contributors'}
              </div>
            </div>
        );
    }
);

const ElevationStrip = ({ prof, accent, units }) => {
    const W = 524;
    const H = 60;
    const eles = prof.points.map((p) => p.ele).filter((e) => e != null);
    const lo = Math.min(...eles);
    const hi = Math.max(...eles);
    const span = hi - lo || 1;
    const sx = (d) => (d / prof.totalDist) * W;
    const sy = (e) => H - ((e - lo) / span) * H;
    const line = prof.points.filter((p) => p.ele != null).map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.dist).toFixed(1)},${sy(p.ele).toFixed(1)}`).join(' ');
    const area = `M0,${H} ` + prof.points.filter((p) => p.ele != null).map((p) => `L${sx(p.dist).toFixed(1)},${sy(p.ele).toFixed(1)}`).join(' ') + ` L${W},${H} Z`;
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', color: PALETTE.textMuted }}>ELEVATION</span>
                <span style={{ fontWeight: 600, fontSize: 11, color: PALETTE.textFaint }}>{formatElevation(lo, units)}–{formatElevation(hi, units)}</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} preserveAspectRatio="none">
                <path d={area} fill={accent} fillOpacity="0.14" />
                <path d={line} fill="none" stroke={accent} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            </svg>
        </>
    );
};
ElevationStrip.propTypes = { prof: PropTypes.object.isRequired, accent: PropTypes.string.isRequired, units: PropTypes.string.isRequired };

TrailMapRender.displayName = 'TrailMapRender';
TrailMapRender.propTypes = { segments: PropTypes.array.isRequired, title: PropTypes.string, accent: PropTypes.string, units: PropTypes.oneOf(['metric', 'imperial']), styleId: PropTypes.string, show: PropTypes.object, statFields: PropTypes.object, onReady: PropTypes.func };

export default TrailMapRender;
